import {h, render} from 'preact';
import lazy from 'preact-lazy';
import {
  prepareCategories,
  prepareFeaturedQuestions,
  prepareWidgetSetting,
  prepareTranslatedFaqs,
  prepareTranslatedQuestion,
  prepareTranslatedFeaturedQuestion,
  prepareCampaignSettings,
  prepareMetafields,
  prepareQuestions,
  getAppStatusMetafields
} from '../../helpers/faqHelpers';
import ApiManager from '../../managers/ApiManager';
import canShowWidget from '@assets/helpers/widget/canShowWidget';
import logToConsole from '@scripttag/helpers/logToConsole';
import {setWidgetSettingsStorage} from '@scripttag/helpers/sessionHelpers';
import isCurrentDeviceAllowed from '@scripttag/helpers/utils/isCurrentDeviceAllowed';

const ParentWidget = lazy(() => import('@assets/components/Preview/ParentWidget'));
const ChatBoxContainer = lazy(() => import('@assets/components/Preview/ChatBoxContainer'));

/** @jsx h Tell babel to use h for JSX */
export default class DisplayManager {
  initialize = () => {
    logToConsole('Chatty chatbox has initialized');

    if (window.AVADA_APP_FAQS_STATUS) {
      return this.renderFloatingButton();
    }

    window.AVADA_APP_FAQS_STATUS = {}; // to get in checkout page
    setWidgetSettingsStorage({open: false}); // to avoid auto open chatbox on checkout page
    this.prepareMetafields().then();
  };

  prepareMetafields = async () => {
    try {
      const apiManager = new ApiManager();
      const postData = (path, data) => apiManager.postData(path, data);

      await getAppStatusMetafields(postData);
    } catch (error) {
      console.log('Error preparing metafields:', error);
    } finally {
      this.renderFloatingButton();
    }
  };

  renderFloatingButton = async () => {
    const {shopId} = window.AVADA_APP_FAQS_STATUS;
    const apiManager = new ApiManager();
    const getData = (path, params) => apiManager.getData(shopId, path, params);
    const putData = (path, data) => apiManager.putData(path, {...data, shopId});
    const postData = (path, data) => apiManager.postData(path, {...data, shopId});

    await prepareMetafields(postData);

    const {translation} = window.AVADA_FAQ;
    const settings = prepareWidgetSetting();
    if (!canShowWidget(settings)) return;

    const campaigns = prepareCampaignSettings();
    const questions = prepareQuestions();
    const categories = prepareCategories();
    const faqsTranslation = prepareTranslatedFaqs();
    const featuredQuestions = prepareFeaturedQuestions();
    const translatedQuestions = prepareTranslatedQuestion();
    const translatedFeaturedQuestions = prepareTranslatedFeaturedQuestion();

    const wrapper = document.createElement('div');
    wrapper.id = 'Avada-FAQ_Widget';
    this.insertWrapperIntoDOM(wrapper);

    const customCss = document.createElement('style');
    customCss.innerHTML = settings.customCss;
    wrapper.appendChild(customCss);

    const components = settings.isLiveChat
      ? document.querySelectorAll('.Avada-Faqs-Chatbox-Block-Container')
      : [];

    [...components, wrapper].forEach(component => {
      const isEmbedBlock = component.id === 'Avada-FAQ_Widget';
      const dataSettings = (!isEmbedBlock && JSON.parse(component.dataset.userInfo)) || {};
      const blockId = (!isEmbedBlock && component.dataset.blockId) || '';
      const {device} = dataSettings;
      if (!isCurrentDeviceAllowed(device)) return;

      const RenderElement = props => {
        return isEmbedBlock ? (
          <ParentWidget {...props} />
        ) : (
          <ChatBoxContainer {...{...props, shopId, dataSettings, blockId}} />
        );
      };

      render(
        <RenderElement
          {...{
            translation,
            settings,
            campaigns,
            questions,
            categories,
            faqsTranslation,
            featuredQuestions,
            translatedQuestions,
            translatedFeaturedQuestions,
            isPreview: false,
            getData,
            putData,
            postData
          }}
        />,
        component
      );
    });
  };

  insertWrapperIntoDOM = wrapper => {
    const skipToContentLink = document.querySelector(
      'a[href="#main"], a[href="#MainContent"], a[href="#content"], a[href^="#skip-to-content"]'
    );

    try {
      if (!skipToContentLink) {
        document.body.insertBefore(wrapper, document.body.firstChild);
        return;
      }

      if (skipToContentLink.nextSibling && skipToContentLink.parentNode === document.body) {
        // First try to insert after the skip link
        document.body.insertBefore(wrapper, skipToContentLink.nextSibling);
      } else if (
        skipToContentLink.parentNode &&
        skipToContentLink.parentNode.nextSibling &&
        skipToContentLink.parentNode.parentNode === document.body
      ) {
        // Then try to insert after the skip link's parent
        document.body.insertBefore(wrapper, skipToContentLink.parentNode.nextSibling);
      } else {
        // Default to first child if the above conditions aren't met
        document.body.insertBefore(wrapper, document.body.firstChild);
      }
    } catch (error) {
      // Fallback if any errors occur
      document.body.insertBefore(wrapper, document.body.firstChild);
      console.warn('Fallback inserting chatty', error);
    }
  };
}
